import { useEffect, useState } from "react";
import PageShell from "@/components/PageShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PaperPlaneTilt, Sparkle, ClockCounterClockwise, UserPlus, X, Envelope, Warning } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const shortDate = (d) => (d ? String(d).slice(0, 16).replace("T", " ") : "—");
const CADENCES = [
  { value: "weekly", label: "Weekly · every Monday" },
  { value: "biweekly", label: "Biweekly · every other Monday" },
  { value: "monthly", label: "Monthly · roughly first Monday" },
];

export default function FirmBrief() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [recipients, setRecipients] = useState("");
  const [history, setHistory] = useState([]);
  const [subs, setSubs] = useState([]);
  const [subPreview, setSubPreview] = useState([]);
  const [newSubEmail, setNewSubEmail] = useState("");
  const [newSubCadence, setNewSubCadence] = useState("weekly");
  const [historyEmail, setHistoryEmail] = useState(null); // opens the per-subscriber history dialog
  const [historyData, setHistoryData] = useState(null);

  const openHistory = async (email) => {
    setHistoryEmail(email);
    setHistoryData(null);
    try {
      const { data } = await api.get(`/briefs/subscriptions/${encodeURIComponent(email)}/history`);
      setHistoryData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load history");
      setHistoryEmail(null);
    }
  };

  const loadPreview = async () => {
    setLoading(true);
    try {
      const p = await api.get("/briefs/preview");
      setPreview(p.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load brief");
    } finally {
      setLoading(false);
    }
  };
  const loadHistory = async () => {
    try { const h = await api.get("/briefs/history"); setHistory(h.data); }
    catch (err) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("[firm-brief] history load failed", err);
      }
    }
  };
  const loadSubs = async () => {
    try {
      const [r, pv] = await Promise.all([
        api.get("/briefs/subscriptions"),
        api.get("/briefs/subscriptions/preview"),
      ]);
      setSubs(r.data);
      setSubPreview(pv.data);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("[firm-brief] subs load failed", err);
      }
    }
  };
  useEffect(() => {
    // First mount only — Claude runs once, subscriptions + history load in parallel.
    loadPreview();
    loadHistory();
    loadSubs();
  }, []);

  const sendNow = async () => {
    const list = recipients.split(",").map((s) => s.trim()).filter(Boolean);
    setSending(true);
    try {
      const { data } = await api.post("/briefs/send-now", { recipients: list.length ? list : null });
      if (data.sent) {
        toast.success(`Brief sent to ${data.recipients.join(", ")} · email_id ${(data.email_id || "").slice(0, 8)}`, {
          description: data.reply_to
            ? `Sender reply-to: ${data.reply_to}. Check your spam/Promotions folder if it hasn't arrived in a minute.`
            : "Set a firm email in Settings so replies come back to you — and check your spam/Promotions folder if the message isn't in your inbox yet.",
          duration: 10000,
        });
      } else {
        toast.error(data.reason || "Not sent");
      }
      loadHistory();
      loadSubs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const addSub = async () => {
    if (!newSubEmail) return toast.error("Email required");
    try {
      await api.post("/briefs/subscriptions", { email: newSubEmail, cadence: newSubCadence });
      setNewSubEmail("");
      setNewSubCadence("weekly");
      loadSubs();
      toast.success(`${newSubEmail} subscribed · ${newSubCadence}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not subscribe");
    }
  };
  const changeCadence = async (email, cadence) => {
    try { await api.post("/briefs/subscriptions", { email, cadence }); loadSubs(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const removeSub = async (email) => {
    try { await api.delete(`/briefs/subscriptions/${encodeURIComponent(email)}`); loadSubs(); toast.success(`${email} unsubscribed`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
  };

  return (
    <PageShell size="content" testid="firm-brief-page">
      <div className="mb-6">
        <div className="text-[0.625rem] font-mono uppercase tracking-[0.2em] text-primary">Weekly Strategic Pulse</div>
        <h1 className="mt-2 font-display text-3xl font-bold tracking-tight">Monday brief</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Claude-authored partner brief · Monday 08:00 UTC cron respects each recipient's cadence · one-click Send Now bypasses cadence.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-border bg-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <Sparkle size={12} weight="fill" className="text-primary" /> AI-drafted preview
            </div>
            <button
              onClick={loadPreview}
              disabled={loading}
              className="text-[0.6875rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary"
              data-testid="brief-refresh"
            >
              {loading ? "Generating…" : "Regenerate"}
            </button>
          </div>
          {loading && <div className="text-sm text-muted-foreground">Composing brief…</div>}
          {!loading && preview && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4 text-center">
                {Object.entries({
                  Clients: preview.context.clients,
                  Active: preview.context.active_engagements,
                  "At-risk": preview.context.at_risk_clients,
                  "Inv 7d": `$${(preview.context.invoiced_last_7d || 0).toLocaleString()}`,
                }).map(([k, v]) => (
                  <div key={k} className="border border-border/60 bg-secondary/30 py-3">
                    <div className="text-lg font-display font-bold text-primary data-cell">{v}</div>
                    <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-0.5">{k}</div>
                  </div>
                ))}
              </div>
              {/*
                Iter 91.7 · Health-shifts drill-through. Renders the freshest
                partner-org signal that the Monday brief now includes as
                section (7) HEALTH SHIFTS — clickable so a partner reading
                the brief lands on the affected Partner page in one hop.
                Silent empty state matches the AI brief's rule of omitting
                the section entirely on a quiet week.
              */}
              {Array.isArray(preview.context.health_degradations) && preview.context.health_degradations.length > 0 && (
                <div className="mb-4 border border-brass/40 bg-brass/[0.04] p-4" data-testid="firm-brief-health-shifts">
                  <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow mb-2 flex items-center gap-1.5">
                    <Warning size={12} weight="fill" className="text-brass" />
                    Health shifts · last 7 days ({preview.context.health_degradations.length})
                  </div>
                  <ul className="space-y-1.5">
                    {preview.context.health_degradations.map((row) => {
                      const daysAgo = row.days_ago;
                      const when = daysAgo === 0 ? "today" : daysAgo === 1 ? "yesterday" : `${daysAgo}d ago`;
                      return (
                        <li key={row.client_id} data-testid={`health-shift-${row.client_id}`}>
                          <Link
                            to={`/clients/${row.client_id}`}
                            className="group flex items-baseline justify-between gap-3 text-sm hover:bg-secondary/40 -mx-2 px-2 py-1 transition-colors"
                          >
                            <span className="font-serif font-semibold truncate group-hover:text-primary transition-colors">
                              {row.client_name}
                            </span>
                            <span className="text-[0.6875rem] font-mono uppercase tracking-widest text-muted-foreground shrink-0">
                              {row.previous_label} → <span className="text-destructive">{row.current_label}</span> · {when}
                            </span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              <pre
                data-testid="brief-preview-text"
                className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground bg-secondary/20 border border-border/50 p-5"
              >
                {preview.text}
              </pre>
            </>
          )}
        </div>

        <div className="flex flex-col gap-4">
          <div className="border border-border bg-card p-6">
            <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2">
              <PaperPlaneTilt size={12} weight="fill" /> Send now (bypasses cadence)
            </div>
            <Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">
              Recipients (blank = all subscribers)
            </Label>
            <Input
              data-testid="brief-recipients"
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              placeholder="cfo@firm.com, partner@firm.com"
              className="rounded-none bg-secondary/40 mt-1"
            />
            <Button
              data-testid="brief-send-btn"
              onClick={sendNow}
              disabled={sending}
              className="mt-3 w-full rounded-none bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <PaperPlaneTilt size={14} weight="bold" className="mr-2" />
              {sending ? "Sending…" : "Send brief now"}
            </Button>
          </div>

          <div className="border border-border bg-card p-6">
            <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
              <UserPlus size={12} weight="fill" /> Cadence subscriptions
            </div>
            {subPreview.length > 0 && (
              <div className="mb-3 border border-primary/40 bg-primary/5 px-3 py-2" data-testid="cadence-preview">
                <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary mb-1">
                  Next Monday would send to {subPreview.filter(r => r.would_send).length} of {subPreview.length}
                </div>
                <ul className="text-[0.6875rem] font-mono space-y-0.5">
                  {subPreview.map((r) => (
                    <li key={r.email} className={r.would_send ? "text-foreground" : "text-muted-foreground/70"}>
                      <button
                        onClick={() => openHistory(r.email)}
                        data-testid={`cadence-preview-${r.email}`}
                        className="text-left w-full hover:text-primary transition-colors flex items-start gap-2"
                        title="Click to see this subscriber's send history"
                      >
                        <span className={`inline-block w-1.5 h-1.5 mt-1.5 rounded-full shrink-0 ${r.would_send ? "bg-primary" : "bg-muted-foreground/40"}`} />
                        <span className="truncate">{r.email} · {r.reason}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex gap-2 mb-3">
              <Input
                data-testid="sub-new-email"
                value={newSubEmail}
                onChange={(e) => setNewSubEmail(e.target.value)}
                placeholder="analyst@firm.com"
                className="rounded-none bg-secondary/40 text-xs"
              />
              <Select value={newSubCadence} onValueChange={setNewSubCadence}>
                <SelectTrigger data-testid="sub-new-cadence" className="rounded-none bg-secondary/40 text-xs w-36"><SelectValue /></SelectTrigger>
                <SelectContent>{CADENCES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
              <Button data-testid="sub-add-btn" onClick={addSub} size="sm" className="rounded-none bg-primary text-primary-foreground text-xs">Add</Button>
            </div>
            {subs.length === 0 && (
              <div className="text-xs text-muted-foreground">No subscribers yet — the Monday cron falls back to all Partner users if this list stays empty.</div>
            )}
            <ul className="space-y-2" data-testid="sub-list">
              {subs.map((sub) => (
                <li key={sub.email} className="flex items-center gap-2 text-xs font-mono border-l-2 border-primary/40 pl-3 py-1" data-testid={`sub-row-${sub.email}`}>
                  <div className="flex-1 truncate">
                    <div className="text-foreground truncate">{sub.email}</div>
                    <div className="text-[0.625rem] text-muted-foreground">last: {shortDate(sub.last_sent_at)}</div>
                  </div>
                  <Select value={sub.cadence} onValueChange={(v) => changeCadence(sub.email, v)}>
                    <SelectTrigger className="rounded-none bg-secondary/40 h-8 w-32 text-xs" data-testid={`sub-cadence-${sub.email}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{CADENCES.map(c => <SelectItem key={c.value} value={c.value}>{c.value}</SelectItem>)}</SelectContent>
                  </Select>
                  <button onClick={() => removeSub(sub.email)} className="text-muted-foreground hover:text-destructive p-1" data-testid={`sub-remove-${sub.email}`}>
                    <X size={14} weight="bold" />
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="border border-border bg-card p-6">
            <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2">
              <ClockCounterClockwise size={12} /> Recent sends
            </div>
            {history.length === 0 && (
              <div className="text-xs text-muted-foreground">No brief has been sent yet.</div>
            )}
            <ul className="space-y-2" data-testid="brief-history">
              {history.slice(0, 6).map((h, i) => (
                <li key={i} className="text-xs font-mono border-l-2 border-primary/40 pl-3 py-1">
                  <div className="text-foreground">{shortDate(h.sent_at)}</div>
                  <div className="text-muted-foreground truncate">to {(h.recipients || []).join(", ") || "—"}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <Dialog open={!!historyEmail} onOpenChange={(v) => { if (!v) { setHistoryEmail(null); setHistoryData(null); } }}>
        <DialogContent className="rounded-none bg-card max-w-2xl" data-testid="subscriber-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Envelope size={16} weight="fill" className="text-primary" /> Send history · {historyEmail}
            </DialogTitle>
          </DialogHeader>
          {!historyData && <div className="text-sm text-muted-foreground py-6">Loading history…</div>}
          {historyData && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-center">
                <div className="border border-border/60 bg-secondary/30 py-3">
                  <div className="text-lg font-display font-bold text-primary data-cell" data-testid="subscriber-history-count">{historyData.total_sends}</div>
                  <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-0.5">Total sends</div>
                </div>
                <div className="border border-border/60 bg-secondary/30 py-3">
                  <div className="text-lg font-display font-bold text-muted-foreground data-cell">
                    {historyData.open_rate === null ? "—" : Math.round(historyData.open_rate * 100) + "%"}
                  </div>
                  <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-0.5">Open rate</div>
                </div>
              </div>
              {historyData.open_rate === null && (
                <div className="text-[0.6875rem] font-mono text-muted-foreground border-l-2 border-primary/40 pl-3">
                  Open rate turns on once Resend webhook events are wired. Today YadidOS only sees what got sent.
                </div>
              )}
              {historyData.history.length === 0 && (
                <div className="text-sm text-muted-foreground">This subscriber hasn't been sent a brief yet.</div>
              )}
              <ul className="space-y-2 max-h-96 overflow-y-auto pr-2" data-testid="subscriber-history-list">
                {historyData.history.map((h, i) => (
                  <li key={i} className="border-l-2 border-primary/40 pl-3 py-1.5">
                    <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary">{shortDate(h.sent_at)}</div>
                    <div className="text-xs text-muted-foreground truncate">email_id {String(h.email_id || "").slice(0, 8) || "—"} · reply-to {h.reply_to || "unset"}</div>
                    {h.brief_preview && (
                      <div className="text-xs text-foreground/80 mt-1 line-clamp-3">{h.brief_preview}</div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
